import { FC, forwardRef, ReactNode } from "react"

import { ReactComponent as IconCheckCircle } from "@common_assets/svg/check-circle.svg"
import { ReactComponent as IconWarningCircle } from "@common_assets/svg/warning-circle.svg"
import { Box, BoxProps, Stack } from "@mui/material"
import Coin from "components/common/icon/coin"
import IconWrapper from "components/common/icon/iconWrapper"
import { useIsBreakpointUp } from "hooks/breakpoint"

// Coin's inline svg has descender whitespace; block display re-centres the amount.
// `fontSize` (px) scales the coin with the amount for emphasised totals.
export const Price: FC<{ amount: number | string; fontSize?: number }> = ({
  amount,
  fontSize,
}) => (
  <Box sx={{ display: "flex", "& svg": { display: "block" } }}>
    <Coin
      amount={amount}
      size="small"
      {...(fontSize && {
        iconSize: `${fontSize}px`,
        valueTextProps: {
          fontSize: `${fontSize}px`,
          lineHeight: `${Math.round(fontSize * 1.2)}px`,
        },
      })}
    />
  </Box>
)

const DONE_ICON_TONE = {
  success: { bg: "mockup.success90", color: "mockup.success30" },
  warning: { bg: "mockup.secondary90", color: "mockup.neutral20" },
} as const

export const DoneIcon: FC<{ tone: keyof typeof DONE_ICON_TONE }> = ({
  tone,
}) => (
  <Box
    aria-hidden
    sx={{
      flexShrink: 0,
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: DONE_ICON_TONE[tone].bg,
    }}
  >
    <IconWrapper
      width="40px"
      height="40px"
      sx={{ "& svg": { display: "block", width: "100%", height: "100%" } }}
      color={DONE_ICON_TONE[tone].color}
    >
      {tone === "warning" ? <IconWarningCircle /> : <IconCheckCircle />}
    </IconWrapper>
  </Box>
)

// Below tablet the table scrolls sideways in a focusable region, so keyboard users can scroll it.
export const TableScrollRegion: FC<{
  labelledBy: string
  children: ReactNode
}> = ({ labelledBy, children }) => {
  const isTablet = useIsBreakpointUp("tablet")
  if (isTablet) return children

  return (
    <Box
      tabIndex={0}
      role="region"
      aria-labelledby={labelledBy}
      sx={{ overflowX: "auto", "& .MuiTable-root": { minWidth: "560px" } }}
    >
      {children}
    </Box>
  )
}

const NOTICE_BG = {
  error: "mockup.error90",
  warning: "mockup.secondary90",
} as const

interface INoticeProps extends Omit<BoxProps, "ref"> {
  tone: keyof typeof NOTICE_BG
  // Leading icon turns the notice into a row (the 409 banner).
  icon?: ReactNode
}

// Tinted message block; callers pass their own Typography (variants differ per message).
export const Notice = forwardRef<HTMLDivElement, INoticeProps>(
  ({ tone, icon, children, ...boxProps }, ref) =>
    icon ? (
      <Stack
        ref={ref}
        {...boxProps}
        direction="row"
        alignItems="center"
        gap={1}
        px={1.75}
        py={1.5}
        sx={{ bgcolor: NOTICE_BG[tone], borderRadius: "8px" }}
      >
        {icon}
        {children}
      </Stack>
    ) : (
      <Box
        ref={ref}
        {...boxProps}
        px={1.75}
        py={1.5}
        sx={{ bgcolor: NOTICE_BG[tone], borderRadius: "8px" }}
      >
        {children}
      </Box>
    ),
)
Notice.displayName = "Notice"
